/**
 * FixedAssetListPage — Targyi eszkoz lista
 * Epic 66: Tárgyi Eszköz Nyilvántartás & ÉCS
 */

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useCreateFixedAsset, useFixedAssets, useRunDepreciation } from '@/hooks/useFixedAsset';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(amount);
}

// Labels moved inside component to use t() — see CATEGORY_LABELS / STATUS_LABELS below

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  FULLY_DEPRECIATED: 'bg-yellow-100 text-yellow-800',
  SCRAPPED: 'bg-red-100 text-red-800',
  SOLD: 'bg-muted text-foreground',
};

export function FixedAssetListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const CATEGORY_LABELS: Record<string, string> = {
    MACHINERY: t('finance.categoryLabels.machinery'),
    IT_EQUIPMENT: t('finance.categoryLabels.itEquipment'),
    VEHICLE: t('finance.categoryLabels.vehicle'),
  };

  const STATUS_LABELS: Record<string, string> = {
    ACTIVE: t('finance.assetStatusLabels.active'),
    FULLY_DEPRECIATED: t('finance.assetStatusLabels.fullyDepreciated'),
    SCRAPPED: t('finance.assetStatusLabels.scrapped'),
    SOLD: t('finance.assetStatusLabels.sold'),
  };
  const [filter, setFilter] = useState<{ category?: string; status?: string }>({});
  const { result, isLoading, error } = useFixedAssets(filter);
  const createAsset = useCreateFixedAsset();
  const runDepr = useRunDepreciation();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: '',
    category: 'MACHINERY',
    purchasePrice: '',
    commissionDate: '',
    inventoryNumber: '',
    serialNumber: '',
    note: '',
  });

  const handleCreate = async () => {
    const price = parseInt(form.purchasePrice, 10);
    if (!form.name || !form.commissionDate || !price || price < 200000) return;

    await createAsset.mutateAsync({
      name: form.name,
      category: form.category,
      purchasePrice: price,
      commissionDate: form.commissionDate,
      inventoryNumber: form.inventoryNumber || undefined,
      serialNumber: form.serialNumber || undefined,
      note: form.note || undefined,
    });

    setForm({
      name: '',
      category: 'MACHINERY',
      purchasePrice: '',
      commissionDate: '',
      inventoryNumber: '',
      serialNumber: '',
      note: '',
    });
    setShowForm(false);
  };

  const handleRunDepreciation = async () => {
    const now = new Date();
    await runDepr.mutateAsync({ year: now.getFullYear(), month: now.getMonth() + 1 });
  };

  if (isLoading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">{t('finance.fixedAssetsTitle')}</h1>
        <div className="text-center py-8 text-muted-foreground">{t('finance.loadingData')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">{t('finance.fixedAssetsTitle')}</h1>
        <Card>
          <CardContent className="p-8 text-center text-red-500">
            {t('finance.errorOccurred', { message: error })}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">{t('finance.fixedAssetsTitle')}</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRunDepreciation} disabled={runDepr.isPending}>
            {runDepr.isPending ? t('finance.runningDepreciation') : t('finance.runDepreciation')}
          </Button>
          <Button onClick={() => setShowForm(!showForm)}>
            {showForm ? t('common.cancel') : t('finance.newAsset')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4">
        <select
          className="border rounded px-3 py-2 bg-background text-sm"
          value={filter.category ?? ''}
          onChange={e => setFilter({ ...filter, category: e.target.value || undefined })}
        >
          <option value="">{t('finance.allCategories')}</option>
          <option value="MACHINERY">{t('finance.categoryLabels.machinery')}</option>
          <option value="IT_EQUIPMENT">{t('finance.categoryLabels.itEquipment')}</option>
          <option value="VEHICLE">{t('finance.categoryLabels.vehicle')}</option>
        </select>
        <select
          className="border rounded px-3 py-2 bg-background text-sm"
          value={filter.status ?? ''}
          onChange={e => setFilter({ ...filter, status: e.target.value || undefined })}
        >
          <option value="">{t('finance.allStatuses')}</option>
          <option value="ACTIVE">{t('finance.assetStatusLabels.active')}</option>
          <option value="FULLY_DEPRECIATED">
            {t('finance.assetStatusLabels.fullyDepreciated')}
          </option>
          <option value="SCRAPPED">{t('finance.assetStatusLabels.scrapped')}</option>
        </select>
      </div>

      {showForm && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('finance.newAssetForm')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Input
                placeholder={t('finance.assetName')}
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
              />
              <select
                className="border rounded px-3 py-2 bg-background"
                value={form.category}
                onChange={e => setForm({ ...form, category: e.target.value })}
              >
                <option value="MACHINERY">{t('finance.categoryLabels.machinery')}</option>
                <option value="IT_EQUIPMENT">{t('finance.categoryLabels.itEquipment')}</option>
                <option value="VEHICLE">{t('finance.categoryLabels.vehicle')}</option>
              </select>
              <Input
                type="number"
                placeholder={t('finance.purchasePricePlaceholder')}
                value={form.purchasePrice}
                onChange={e => setForm({ ...form, purchasePrice: e.target.value })}
              />
              <Input
                type="date"
                placeholder={t('finance.commissionDate')}
                value={form.commissionDate}
                onChange={e => setForm({ ...form, commissionDate: e.target.value })}
              />
              <Input
                placeholder={t('finance.inventoryNumber')}
                value={form.inventoryNumber}
                onChange={e => setForm({ ...form, inventoryNumber: e.target.value })}
              />
              <Input
                placeholder={t('finance.serialNumber')}
                value={form.serialNumber}
                onChange={e => setForm({ ...form, serialNumber: e.target.value })}
              />
            </div>
            <Button className="mt-3" onClick={handleCreate} disabled={createAsset.isPending}>
              {createAsset.isPending ? t('finance.recording') : t('finance.recordAsset')}
            </Button>
          </CardContent>
        </Card>
      )}

      {runDepr.isSuccess && (
        <Card className="mb-4 border-green-500">
          <CardContent className="py-3 text-center text-green-700">
            {t('finance.depreciationComplete', {
              processed: runDepr.data?.data?.processed ?? 0,
              deprecated: runDepr.data?.data?.fullyDepreciated ?? 0,
            })}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('finance.assetsCount', { count: result?.total ?? 0 })}</CardTitle>
        </CardHeader>
        <CardContent>
          {!result?.assets.length ? (
            <p className="text-muted-foreground text-center py-4">{t('finance.noAssets')}</p>
          ) : (
            <div className="divide-y">
              {result.assets.map(asset => (
                <div
                  key={asset.id}
                  className="py-3 flex justify-between items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 px-2 -mx-2 rounded"
                  onClick={() => navigate(`/finance/fixed-assets/${asset.id}`)}
                >
                  <div>
                    <span className="font-medium">{asset.name}</span>
                    <span className="ml-3 text-sm text-muted-foreground">
                      {CATEGORY_LABELS[asset.category] ?? asset.category}
                    </span>
                    <span
                      className={`ml-2 text-xs px-2 py-1 rounded ${STATUS_COLORS[asset.status] ?? ''}`}
                    >
                      {STATUS_LABELS[asset.status] ?? asset.status}
                    </span>
                    {asset.inventoryNumber && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        #{asset.inventoryNumber}
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="font-medium">{formatCurrency(asset.currentValue)}</div>
                    <div className="text-xs text-muted-foreground">
                      {t('finance.purchaseLabel')} {formatCurrency(asset.purchasePrice)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
